import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Calendar, Clock, FileText, Search } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { motion, useInView } from 'framer-motion';
import SEO, { SITE_URL } from '../components/SEO';

const blogPosts = [
  {
    id: 7,
    title: "When You're Building Innocent Side Projects… and the German Police Email You",
    summary:
      'One ordinary day, I received an email from my VPS provider about suspicious activity detected by the German Federal Office for Information Security. A story about outdated dependencies, cryptocurrency mining, and lessons learned the hard way.',
    markdownPath: '/blog-md/GermanPolice.md',
    coverImage: '/blog-images/GermanPolice.png',
    headerMedia: {
      type: 'image',
      url: '/blog-images/GermanPolice.png',
      poster: '/blog-images/GermanPolice.png',
      caption: "When You're Building Innocent Side Projects… and the German Police Email You",
    },
    date: '2026-01-13',
    readTime: '8 min',
    tags: ['Security', 'DevOps', 'VPS', 'Dependencies', 'Lessons Learned'],
    author: 'Kajetan Jędrych',
  },
  {
    id: 6,
    title: "I Just Finished My Master's Degree at a Private University in Poland — Was It Worth It?",
    summary:
      'A candid look at my 20-year educational journey, the real costs of private university, and whether that engineering diploma actually paid off — with a fair share of pros, cons, and honest reflections.',
    markdownPath: '/blog-md/University.md',
    coverImage: '/blog-images/University.png',
    headerMedia: {
      type: 'image',
      url: '/blog-images/University.png',
      poster: '/blog-images/University.png',
      caption: "I Just Finished My Master's Degree at a Private University in Poland — Was It Worth It?",
    },
    date: '2025-08-03',
    readTime: '12 min',
    tags: ['Education', 'Career', 'Private University', 'IT', 'Poland'],
    author: 'Kajetan Jędrych',
  },
  {
    id: 5,
    title: 'From Audio Drivers to AI Testing: My Journey into AI testing',
    summary:
      'A hands-on dive into testing AI models – from performance tuning to LLM evaluation and security testing, all inspired by a real-world project.',
    markdownPath: '/blog-md/LlmTesting.md',
    coverImage: '/blog-images/LlmTesting.png',
    headerMedia: {
      type: 'image',
      url: '/blog-images/LlmTesting.png',
      poster: '/blog-images/LlmTesting.png',
      caption: 'From Audio Drivers to AI Testing: My Journey into AI testing',
    },
    date: '2025-05-16',
    readTime: '10 min',
    tags: ['AI Testing', 'LLM', 'Machine Learning'],
    author: 'Kajetan Jędrych',
  },
  {
    id: 4,
    title: 'How I Found the Perfect Budget-Friendly Way to Deploy My Apps',
    summary: 'Discover how I deploy my apps – step-by-step instructions included.',
    markdownPath: '/blog-md/Coolify.md',
    coverImage: '/blog-images/coolify.png',
    headerMedia: {
      type: 'image',
      url: '/blog-images/coolify.png',
      poster: '/blog-images/coolify.png',
      caption: 'How I Found the Perfect Budget-Friendly Way to Deploy My Apps',
    },
    date: '2025-04-29',
    readTime: '10 min',
    tags: ['Coolify', 'Deployment', 'Instructions'],
    author: 'Kajetan Jędrych',
  },
  {
    id: 1,
    title: 'Building a Website Just for Myself',
    summary:
      'A personal story of building and launching my first solo project — just for me, but finished and online.',
    markdownPath: '/blog-md/FirstProjectBlogPost.md',
    coverImage: '/blog-images/KalkulatoryOrg.png',
    headerMedia: null,
    date: '2025-04-19',
    readTime: '5 min',
    tags: ['React', 'Next.js', 'Web Development'],
    author: 'Kajetan Jędrych',
  },
  {
    id: 2,
    title: 'Brilliant Idea (But Only in My Head)',
    summary: 'Built an AI-powered app to analyze social media comments — it was brilliant... in my head.',
    markdownPath: '/blog-md/BriliantIdea.md',
    coverImage: '/blog-images/Gemini.png',
    headerMedia: {
      type: 'video',
      url: '/blog-images/SummaryAiVideo.mp4',
      poster: '/blog-images/SummaryAiPhoto.png',
      caption: 'Short clip of my application',
    },
    date: '2025-04-21',
    readTime: '6 min',
    tags: ['React', 'Next.js', 'Web Development', 'GeminiAi'],
    author: 'Kajetan Jędrych',
  },
  {
    id: 3,
    title: 'The Chaos of Starting… and Never Finishing',
    summary: 'Learn some of my methods to finish what you started.',
    markdownPath: '/blog-md/Productive.md',
    coverImage: '/blog-images/Miro.png',
    headerMedia: {
      type: 'image',
      url: '/blog-images/Miro.png',
      poster: '/blog-images/Miro.png',
      caption: 'Short walkthrough of my productivity system',
    },
    date: '2025-04-21',
    readTime: '10 min',
    tags: ['Productivity', 'Time management', 'Tricks and tips'],
    author: 'Kajetan Jędrych',
  },
];

// Dark-theme markdown renderer
const MarkdownRenderer = ({ markdownContent }) => (
  <div className="prose-dark">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ node, children, ...props }) => (
          <h1 className="text-2xl font-bold mb-5 mt-8 text-slate-100" {...props}>{children}</h1>
        ),
        h2: ({ node, children, ...props }) => (
          <h2 className="text-xl font-bold mb-4 mt-8 text-slate-100" {...props}>{children}</h2>
        ),
        h3: ({ node, children, ...props }) => (
          <h3 className="text-lg font-bold mb-3 mt-6 text-slate-200" {...props}>{children}</h3>
        ),
        p: ({ node, children, ...props }) => (
          <p className="mb-5 text-slate-300 leading-relaxed" {...props}>{children}</p>
        ),
        a: ({ node, children, ...props }) => (
          <a className="text-violet-400 hover:text-violet-300 underline transition-colors" {...props}>{children}</a>
        ),
        ul: ({ node, ...props }) => <ul className="mb-5 list-disc pl-6 space-y-1.5" {...props} />,
        ol: ({ node, ...props }) => <ol className="mb-5 list-decimal pl-6 space-y-1.5" {...props} />,
        li: ({ node, ...props }) => <li className="text-slate-300 leading-relaxed" {...props} />,
        blockquote: ({ node, ...props }) => (
          <blockquote
            className="border-l-4 border-violet-500/60 pl-4 italic my-6 text-slate-400 bg-slate-800/50 py-2 rounded-r-lg"
            {...props}
          />
        ),
        em: ({ node, ...props }) => <em className="italic text-slate-300" {...props} />,
        strong: ({ node, ...props }) => <strong className="font-bold text-slate-100" {...props} />,
        hr: ({ node, ...props }) => <hr className="my-8 border-t border-slate-700" {...props} />,
        img: ({ node, ...props }) => (
          <img
            className="rounded-lg shadow-lg my-6 max-w-full h-auto mx-auto"
            {...props}
            alt={props.alt || 'Blog image'}
          />
        ),
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          return !inline && match ? (
            <SyntaxHighlighter
              style={nord}
              language={match[1]}
              PreTag="div"
              className="rounded-lg my-5 text-sm"
              showLineNumbers={true}
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          ) : (
            <code
              className="bg-slate-700/70 text-cyan-300 px-1.5 py-0.5 rounded text-sm font-mono"
              {...props}
            >
              {children}
            </code>
          );
        },
        pre({ node, ...props }) {
          return <pre className="bg-slate-800 p-0 rounded-lg overflow-auto my-5" {...props} />;
        },
      }}
    >
      {markdownContent}
    </ReactMarkdown>
  </div>
);

const BlogCard = ({ post, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <Link to={`/blog/${post.id}`} className="block">
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.4, delay: index * 0.06 }}
        className="group relative bg-slate-800 border border-slate-700 rounded-xl overflow-hidden hover:border-violet-500/40 transition-all duration-300 flex flex-col cursor-pointer"
      >
        {/* Cover image */}
        {post.coverImage && (
          <div className="h-44 overflow-hidden bg-slate-700 flex-shrink-0">
            <div
              className="w-full h-full transform group-hover:scale-105 transition-transform duration-500"
              style={{
                backgroundImage: `url(${post.coverImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          </div>
        )}

        <div className="p-5 flex flex-col flex-grow">
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {post.tags.slice(0, 2).map((tag, i) => (
              <span
                key={i}
                className="text-xs font-medium bg-violet-500/10 text-violet-400 border border-violet-500/20 px-2 py-0.5 rounded-full"
              >
                {tag}
              </span>
            ))}
            {post.tags.length > 2 && (
              <span className="text-xs font-medium bg-slate-700 text-slate-400 px-2 py-0.5 rounded-full">
                +{post.tags.length - 2}
              </span>
            )}
          </div>

          <h3 className="text-base font-bold text-slate-100 group-hover:text-white transition-colors mb-2 leading-snug">
            {post.title}
          </h3>

          <p className="text-slate-400 text-sm leading-relaxed mb-4 flex-grow">{post.summary}</p>

          <div className="flex justify-between items-center pt-4 border-t border-slate-700">
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {post.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {post.readTime}
              </span>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-400 group-hover:text-violet-300 transition-colors">
              <FileText className="w-3.5 h-3.5" />
              Read
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export const BlogList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTag, setActiveTag] = useState('');

  const allTags = [...new Set(blogPosts.flatMap((post) => post.tags))];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.summary.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = activeTag ? post.tags.includes(activeTag) : true;
    return matchesSearch && matchesTag;
  });

  return (
    <section className="bg-slate-900 min-h-screen py-24 px-6">
      <SEO
        title="Blog"
        description="Wpisy o programowaniu, tworzeniu stron internetowych, AI i technologii — blog techniczny Kajetana Jędrycha."
        path="/blog"
      />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-violet-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
            Writing
          </p>
          <h1 className="text-4xl font-bold text-slate-100 mb-4">My Blog</h1>
          <p className="text-slate-400 max-w-xl mx-auto text-sm leading-relaxed">
            Thoughts, tutorials, and insights on web development, AI testing, and technology.
          </p>
          <div className="w-16 h-px bg-gradient-to-r from-violet-500 to-cyan-400 mx-auto mt-4" />
        </motion.div>

        {/* Search + Filter */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mb-10 flex flex-col md:flex-row gap-4 items-start justify-between"
        >
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-full text-slate-200 placeholder-slate-400 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500/50 text-sm transition-all"
            />
          </div>

          <div className="flex gap-2 flex-wrap justify-center">
            <button
              onClick={() => setActiveTag('')}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all duration-200 ${
                activeTag === ''
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-800 border border-slate-700 text-slate-400 hover:border-violet-500/50 hover:text-slate-200'
              }`}
            >
              All
            </button>
            {allTags.map((tag, index) => (
              <button
                key={index}
                onClick={() => setActiveTag(tag)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all duration-200 ${
                  activeTag === tag
                    ? 'bg-violet-600 text-white'
                    : 'bg-slate-800 border border-slate-700 text-slate-400 hover:border-violet-500/50 hover:text-slate-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredPosts.map((post, index) => (
            <BlogCard key={post.id} post={post} index={index} />
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-slate-400">No articles found matching your criteria.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export const BlogPostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [markdownContent, setMarkdownContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const foundPost = blogPosts.find((p) => p.id === parseInt(id));
    if (foundPost) {
      setPost(foundPost);
      setLoadError(false);
      fetch(foundPost.markdownPath)
        .then((response) => {
          if (!response.ok) throw new Error('Failed to load markdown file');
          return response.text();
        })
        .then((content) => {
          const processedContent = content
            .replace(/\*\*([^*]+)\*\*/g, '**$1**')
            .replace(/\*([^*]+)\*/g, '_$1_')
            .replace(/\n\n/g, '\n\n');
          setMarkdownContent(processedContent);
          setIsLoading(false);
          window.scrollTo(0, 0);
        })
        .catch((error) => {
          console.error('Error loading markdown:', error);
          setLoadError(true);
          setIsLoading(false);
        });
    } else {
      navigate('/blog');
    }
  }, [id, navigate]);

  if (!post || isLoading) {
    return (
      <div className="bg-slate-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-slate-900 min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <p className="text-slate-100 font-bold mb-2">This article couldn't be loaded.</p>
          <p className="text-slate-400 text-sm mb-6">
            Something went wrong fetching the content. Please try again.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to all articles
          </Link>
        </div>
      </div>
    );
  }

  const renderHeaderMedia = () => {
    if (post.headerMedia) {
      if (post.headerMedia.type === 'video') {
        return (
          <div className="mb-8">
            <div className="rounded-xl overflow-hidden shadow-xl">
              <video className="w-full" controls poster={post.headerMedia.poster}>
                <source src={post.headerMedia.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            {post.headerMedia.caption && (
              <p className="text-center text-xs text-slate-400 mt-2">{post.headerMedia.caption}</p>
            )}
          </div>
        );
      } else if (post.headerMedia.type === 'image') {
        return (
          <div className="mb-8">
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img
                src={post.headerMedia.url}
                alt={post.headerMedia.caption || post.title}
                className="w-full h-auto"
              />
            </div>
            {post.headerMedia.caption && (
              <p className="text-center text-xs text-slate-400 mt-2">{post.headerMedia.caption}</p>
            )}
          </div>
        );
      }
    }
    if (post.coverImage) {
      return (
        <div className="mb-8">
          <div className="rounded-xl overflow-hidden shadow-xl">
            <img src={post.coverImage} alt={post.title} className="w-full h-auto" />
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <section className="bg-slate-900 min-h-screen py-12 px-6">
      <SEO
        title={post.title}
        description={post.summary}
        path={`/blog/${post.id}`}
        image={`${SITE_URL}${post.coverImage}`}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.summary,
          image: `${SITE_URL}${post.coverImage}`,
          datePublished: post.date,
          author: { '@type': 'Person', name: post.author },
          url: `${SITE_URL}/blog/${post.id}`,
        }}
      />
      <div className="max-w-3xl mx-auto">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-violet-400 transition-colors mb-8 group text-sm"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to all articles
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-slate-800 border border-slate-700 rounded-xl p-8"
        >
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {post.tags.map((tag, i) => (
              <span
                key={i}
                className="text-xs font-medium bg-violet-500/10 text-violet-400 border border-violet-500/20 px-2.5 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100 mb-3 leading-snug">
            {post.title}
          </h1>

          <p className="text-slate-400 mb-6 leading-relaxed text-sm">{post.summary}</p>

          {/* Meta */}
          <div className="flex items-center justify-between text-sm border-b border-slate-700 pb-5 mb-7">
            <span className="font-medium text-slate-300">{post.author}</span>
            <div className="flex items-center gap-4 text-slate-400 text-xs">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {post.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {post.readTime}
              </span>
            </div>
          </div>

          {renderHeaderMedia()}

          <article>
            <MarkdownRenderer markdownContent={markdownContent} />
          </article>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogList;
